/**
 * WebSocket-обвязка kikayoo: подключение к комнате по PIN, отправка конвертов
 * (join/answer/start/next/close), приём и типобезопасный диспатч серверных
 * событий подписчикам. Реконнект с экспоненциальным backoff; повторный join при
 * восстановлении (сервер принимает повторный join идемпотентно — протокол §6).
 *
 * Протокол: см. [`../docs/dev/realtime-protocol.md`](../docs/dev/realtime-protocol.md).
 * Типы: см. [`./protocol.ts`](./protocol.ts).
 */
import {
  type ClientMessage,
  type Role,
  type ServerMessage,
  type StartPayload,
  decodeMessage,
  encodeMessage,
} from './protocol';

/** Состояние соединения для UI (бейдж «подключение…/подключено/отключено»). */
export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closed';

/** Тип host-команды (start/next/close) — принимает только role=host. */
export type HostCommand = 'start' | 'next' | 'close';

/**
 * Терминальные коды ошибок (Фаза 13 B1 / Фаза 15 B1): автореконнект по ним не
 * выполняется — сокет закрывается «как ручное закрытие», сервер следом рвёт
 * соединение. UI получает error-сообщение ДО закрытия и молча завершает сессию.
 */
const TERMINAL_ERROR_CODES: ReadonlySet<string> = new Set(['superseded', 'kicked']);

/** Подписчик на разобранные серверные сообщения. */
export type MessageHandler = (message: ServerMessage) => void;
/** Подписчик на смену статуса соединения. */
export type StatusHandler = (status: SocketStatus) => void;

export interface QuizSocketOptions {
  /** PIN комнаты — попадает в query `/ws?pin=` и в payload join. */
  pin: string;
  /** Роль: host или player. Определяет структуру payload join и доступные команды. */
  role: Role;
  /** Полный WS-URL; по умолчанию строится из window.location (`ws(s)://host/ws?pin=`). */
  url?: string;
  /** Фабрика WebSocket (инъекция для тестов / окружений без DOM). */
  factory?: (url: string) => WebSocket;
  /** Колбэк на каждое открытие соединения (включая реконнект) — здесь шлют `join`. */
  onOpen?: () => void;
  /** Колбэк на фатальное закрытие (реконнекты исчерпаны). */
  onGiveUp?: () => void;
  /** Задержки реконнекта (мс), перебираемые по кругу. */
  reconnectDelays?: number[];
}

const DEFAULT_RECONNECT_DELAYS = [1000, 2000, 4000, 4000, 8000];

/** Строит WS-URL из текущего location (ws/wss) + `?pin=`. Вне DOM — относительный путь. */
export function buildWsUrl(pin: string): string {
  const param = `pin=${encodeURIComponent(pin)}`;
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return `/ws?${param}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws?${param}`;
}

/**
 * QuizSocket — тонкая надстройка над нативным WebSocket: кодирует/декодирует
 * конверты, типизирует события, восстанавливает соединение. Не знает про React.
 */
export class QuizSocket {
  private readonly pin: string;
  private readonly role: Role;
  private readonly url: string;
  private readonly factory: (url: string) => WebSocket;
  private readonly onOpen?: () => void;
  private readonly onGiveUp?: () => void;
  private readonly reconnectDelays: number[];

  private ws: WebSocket | null = null;
  private manuallyClosed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();
  private status: SocketStatus = 'idle';

  constructor(options: QuizSocketOptions) {
    this.pin = options.pin;
    this.role = options.role;
    this.url = options.url ?? buildWsUrl(options.pin);
    this.factory = options.factory ?? ((url: string) => new WebSocket(url));
    this.onOpen = options.onOpen;
    this.onGiveUp = options.onGiveUp;
    this.reconnectDelays = options.reconnectDelays ?? DEFAULT_RECONNECT_DELAYS;
  }

  /** Открывает соединение (идемпотентно: повторный вызов — no-op). */
  connect(): void {
    if (this.ws !== null || this.manuallyClosed) return;
    this.openSocket();
  }

  /** Подписка на разобранные серверные сообщения. Возвращает функцию отписки. */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /** Подписка на смену статуса соединения. Возвращает функцию отписки. */
  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => this.statusHandlers.delete(handler);
  }

  /** Отправляет клиентское сообщение, если соединение открыто (иначе молча). */
  send(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encodeMessage(message));
    }
  }

  /**
   * Отправляет `join` с PIN/ролью/ником. Ник опускается для ведущего.
   * `token` (Фаза 13, Эпик A2) — device-токен игрока из localStorage; подаётся
   * caller'ом ГЕТТЕРОМ на момент отправки (реконнект после F5/выдачи токена
   * должен нести свежее значение). undefined → поле не сериализуется
   * (legacy-клиент без токена, omitempty на сервере).
   */
  sendJoin(nick?: string, token?: string): void {
    this.send({ type: 'join', payload: { pin: this.pin, role: this.role, nick, token } });
  }

  /** Отправляет `answer` (идемпотентный на стороне сервера). */
  sendAnswer(questionId: number, option: number): void {
    this.send({ type: 'answer', payload: { questionId, option } });
  }

  /**
   * Отправляет host-команду start/next/close (только для role=host).
   * Payload-параметр — опции проведения сессии для `start` (scorePause, И4):
   * `next`/`close` всегда шлют пустой `{}` (обратная совместимость).
   */
  sendHostCommand(command: HostCommand, payload?: StartPayload): void {
    if (this.role !== 'host') return;
    if (command === 'start') {
      this.send({ type: 'start', payload: payload ?? {} });
      return;
    }
    this.send({ type: command, payload: {} });
  }

  /** Отправляет `rename` своего ника (только для role=player, фаза lobby). */
  sendRename(nick: string): void {
    if (this.role !== 'player') return;
    this.send({ type: 'rename', payload: { nick } });
  }

  /** Отправляет `rename` чужого ника ведущим (по playerId → nickLocked). */
  sendRenamePlayer(playerId: number, nick: string): void {
    if (this.role !== 'host') return;
    this.send({ type: 'rename', payload: { playerId, nick } });
  }

  /**
   * Отправляет host-команду `kick` (Фаза 15, B1): удалить участника из сессии.
   * Цель — слот (playerId из roster-записи); терминально до конца сессии
   * (повторный вход слота отклоняется). No-op для role=player.
   */
  sendKick(playerId: number): void {
    if (this.role !== 'host') return;
    this.send({ type: 'kick', payload: { playerId } });
  }

  /** Закрывает соединение вручную: отменяет реконнект, статус — closed. */
  close(): void {
    this.manuallyClosed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws !== null) {
      this.removeListeners(this.ws);
      this.ws.close();
      this.ws = null;
    }
    this.setStatus('closed');
  }

  // ─── Внутреннее ────────────────────────────────────────────────────────────

  private openSocket(): void {
    this.setStatus('connecting');
    const ws = this.factory(this.url);
    this.ws = ws;
    ws.addEventListener('open', this.handleOpen);
    ws.addEventListener('message', this.handleMessage);
    ws.addEventListener('error', this.handleError);
    ws.addEventListener('close', this.handleClose);
  }

  private removeListeners(ws: WebSocket): void {
    ws.removeEventListener('open', this.handleOpen);
    ws.removeEventListener('message', this.handleMessage);
    ws.removeEventListener('error', this.handleError);
    ws.removeEventListener('close', this.handleClose);
  }

  private handleOpen = (): void => {
    this.attempt = 0;
    this.setStatus('open');
    // При каждом открытии (включая реконнект) переподключаемся к комнате.
    // Сервер обновляет state, не создаёт дубль игрока (протокол §6).
    this.onOpen?.();
  };

  private handleMessage = (event: MessageEvent): void => {
    const raw = typeof event.data === 'string' ? event.data : '';
    const message = decodeMessage(raw);
    if (message === null) return; // неизвестный/битый тип — игнорируем (§7).
    for (const handler of this.messageHandlers) handler(message);
    // Фаза 13 (Эпик B1): superseded — сокет замещён новым подключением этого
    // слота; сервер закрывает соединение следом. Фаза 15 (Эпик B1): kicked —
    // ведущий удалил участника, код терминальный для слота (как superseded):
    // сервер закрывает соединение, повторный join слота снова получит kicked.
    // В обоих случаях закрываем локально как ручное закрытие (manuallyClosed):
    // иначе handleClose запустил бы цикл реконнекта. Подписчики уже получили
    // сообщение выше (UI показывает конечный экран).
    if (message.type === 'error' && TERMINAL_ERROR_CODES.has(message.payload.code)) {
      this.close();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private handleError = (_event: Event): void => {
    // error не несёт деталей; детали придут в последующем close → реконнект.
  };

  private handleClose = (): void => {
    this.ws = null;
    if (this.manuallyClosed) {
      this.setStatus('closed');
      return;
    }
    this.scheduleReconnect();
  };

  private scheduleReconnect(): void {
    if (this.attempt >= this.reconnectDelays.length) {
      // Попытки исчерпаны — сдаёмся (UI покажет «соединение потеряно»).
      this.setStatus('closed');
      this.onGiveUp?.();
      return;
    }
    const delay = this.reconnectDelays[this.attempt];
    this.attempt += 1;
    this.setStatus('connecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manuallyClosed) this.openSocket();
    }, delay);
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const handler of this.statusHandlers) handler(status);
  }
}
